import React, { useState } from 'react';
import { useParams, useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/api';
import Sidebar from '../components/Sidebar';
import ChatTab from '../components/tabs/ChatTab';
import ProjectConfigTab from '../components/tabs/ProjectConfigTab';
import ConfigurationTab from '../components/tabs/ConfigurationTab';
import CrossProjectTab from '../components/tabs/CrossProjectTab';
import CodeVisualizerTab from '../components/tabs/CodeVisualizerTab';
import WorkflowAnalyzerTab from '../components/tabs/WorkflowAnalyzerTab';
import EvalTestsTab from '../components/tabs/EvalTestsTab';
import FolderView from '../components/FolderView';
import AdminDashboard from '../components/dashboard/AdminDashboard';

const COLLAPSED_SIDEBAR_WIDTH = 64;
const DEFAULT_SIDEBAR_WIDTH = 320;
const MIN_SIDEBAR_WIDTH = 320;
const MAX_SIDEBAR_WIDTH = 600;
const DESKTOP_BREAKPOINT = 768;

const clampSidebarWidth = (value) => Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, value));

const ProjectHub = () => {
  const { projectId, tab, folderId } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { 
    projects, 
    folders,
    currentProject, 
    currentFolder,
    activeTab, 
    isCrossProjectMode, 
    isConfigurationMode,
    isDashboardMode,
    isFolderBrowsingMode
  } = useProject();
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const [lastExpandedSidebarWidth, setLastExpandedSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= DESKTOP_BREAKPOINT);
  const showEvalTests = user?.role === 'admin';

  React.useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSidebarResize = React.useCallback((nextWidth) => {
    const clampedWidth = clampSidebarWidth(Math.round(nextWidth));
    setSidebarWidth(clampedWidth);
    setLastExpandedSidebarWidth(clampedWidth);
  }, []);

  const handleToggleSidebarCollapse = React.useCallback(() => {
    setIsSidebarCollapsed((prev) => {
      if (prev) {
        setSidebarWidth(clampSidebarWidth(lastExpandedSidebarWidth));
        return false;
      }

      setLastExpandedSidebarWidth((previousExpanded) => clampSidebarWidth(sidebarWidth || previousExpanded));
      setSidebarWidth(COLLAPSED_SIDEBAR_WIDTH);
      return true;
    });
  }, [lastExpandedSidebarWidth, sidebarWidth]);
  
  // Refresh user data when entering configuration mode to ensure up-to-date role
  React.useEffect(() => {
    if (isConfigurationMode || isDashboardMode) {
      authService.getCurrentUser()
        .then((response) => {
          setUser(response.data.user);
          // Redirect if not admin
          if (response.data.user?.role !== 'admin') {
            navigate('/');
          }
        })
        .catch((error) => {
          console.error('Failed to verify admin access:', error);
          navigate('/');
        });
    }
  }, [isConfigurationMode, isDashboardMode, navigate, setUser]);
  
  // Get the current project from the URL params
  const displayProject = projectId ? projects.find(p => p.id === projectId) : currentProject;
  
  // Determine current tab from URL or fallback to activeTab
  const currentTab = tab || activeTab;

  // Auto-open config modal when navigating to config tab
  React.useEffect(() => {
    if (currentTab === 'config' && projectId) {
      setShowConfigModal(true);
    }
  }, [currentTab, projectId]);

  // Close config modal handler
  const handleCloseConfigModal = (options = {}) => {
    setShowConfigModal(false);
    if (options.nextPath) {
      navigate(options.nextPath);
      return;
    }

    // If we were on the config tab, navigate back to chat
    if (currentTab === 'config' && projectId) {
      navigate(`/projects/${projectId}/chat`);
    }
  };

  const renderTabContent = () => {
    if (isCrossProjectMode) {
      return <CrossProjectTab />;
    }

    if (isConfigurationMode) {
      // Check if user is admin
      if (user?.role !== 'admin') {
        return (
          <div className="card">
            <div className="flex flex-col items-center justify-center py-12">
              <svg className="w-16 h-16 text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="text-xl font-semibold mb-2" style={{color: 'rgba(255,255,255,0.75)'}}>Access Denied</h3>
              <p className="mb-4" style={{color: 'rgba(255,255,255,0.5)'}}>You need administrator privileges to access this section.</p>
              <button
                onClick={() => navigate('/')}
                className="btn-primary"
              >
                Return to Projects
              </button>
            </div>
          </div>
        );
      }
      return <ConfigurationTab />;
    }

    if (isDashboardMode) {
      if (user?.role !== 'admin') {
        return (
          <div className="card">
            <div className="flex flex-col items-center justify-center py-12">
              <svg className="w-16 h-16 text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="text-xl font-semibold mb-2" style={{color: 'rgba(255,255,255,0.75)'}}>Access Denied</h3>
              <p className="mb-4" style={{color: 'rgba(255,255,255,0.5)'}}>You need administrator privileges to access this section.</p>
              <button
                onClick={() => navigate('/')}
                className="btn-primary"
              >
                Return to Projects
              </button>
            </div>
          </div>
        );
      }
      return <AdminDashboard />;
    }

    if (isFolderBrowsingMode) {
      // Get the folder to display
      const folderToDisplay = currentFolder || {
        name: 'All Projects',
        children: [...(folders.shared || []), ...(folders.personal || [])],
        projects: folders.orphanedShared || []
      };
      
      return <FolderView folder={folderToDisplay} />;
    }

    // Handle project tabs
    if (currentTab === 'code-visualizer') {
      return <CodeVisualizerTab />;
    }

    if (currentTab === 'workflow-analyzer') {
      return <WorkflowAnalyzerTab />;
    }

    if (currentTab === 'eval-tests' && showEvalTests) {
      return <EvalTestsTab />;
    }

    // Default to Chat tab for projects
    return <ChatTab />;
  };

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{backgroundColor: 'var(--tqa-black)'}}>
      {/* Sidebar */}
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={handleToggleSidebarCollapse}
        width={isSidebarCollapsed ? COLLAPSED_SIDEBAR_WIDTH : sidebarWidth}
        minWidth={MIN_SIDEBAR_WIDTH}
        maxWidth={MAX_SIDEBAR_WIDTH}
        isDesktop={isDesktop}
        onResize={handleSidebarResize}
      />

      {/* Main Content */}
      <main
        className="flex-1 main-content ml-0 flex flex-col overflow-hidden px-6 pb-0"
        style={{ marginLeft: isDesktop ? `${isSidebarCollapsed ? COLLAPSED_SIDEBAR_WIDTH : sidebarWidth}px` : 0 }}
      >
        <div className="flex-1 max-w-full mx-auto w-full flex flex-col overflow-hidden min-h-0">
          {/* Project Header (card style) - hidden in cross-project, configuration, and folder browsing modes */}
          {!isCrossProjectMode && !isConfigurationMode && !isDashboardMode && !isFolderBrowsingMode && (
            <>
              <div className="mb-6 flex-shrink-0 pt-6">
                <div className="relative overflow-hidden" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
                  <div className="absolute inset-x-0 top-0 h-px" style={{backgroundColor: 'var(--tqa-green)'}} />
                  <div className="flex items-center justify-between p-5 md:p-6">
                    <div>
                      <h2 className="brand-title text-2xl md:text-3xl">{displayProject?.name || 'Project'}</h2>
                      <p className="text-sm mt-1" style={{color: 'rgba(255,255,255,0.5)'}}>Chat with your project's codebase</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/projects/${projectId}/chat`)}
                        className={`btn-secondary px-4 py-2 flex items-center gap-2 ${currentTab === 'chat' ? 'ring-2 ring-tqa-accent' : ''}`}
                        title="Chat"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        <span className="hidden md:inline">Chat</span>
                      </button>
                      <button
                        onClick={() => navigate(`/projects/${projectId}/code-visualizer`)}
                        className={`btn-secondary px-4 py-2 flex items-center gap-2 ${currentTab === 'code-visualizer' ? 'ring-2 ring-tqa-accent' : ''}`}
                        title="Code Visualizer"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                        </svg>
                        <span className="hidden md:inline">Code</span>
                      </button>
                      <button
                        onClick={() => navigate(`/projects/${projectId}/workflow-analyzer`)}
                        className={`btn-secondary px-4 py-2 flex items-center gap-2 ${currentTab === 'workflow-analyzer' ? 'ring-2 ring-tqa-accent' : ''}`}
                        title="Workflow Analyzer"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                        </svg>
                        <span className="hidden md:inline">Workflow Analyzer</span>
                      </button>
                      {showEvalTests && (
                        <button
                          onClick={() => navigate(`/projects/${projectId}/eval-tests`)}
                          className={`btn-secondary px-4 py-2 flex items-center gap-2 ${currentTab === 'eval-tests' ? 'ring-2 ring-tqa-accent' : ''}`}
                          title="Eval Tests"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="hidden md:inline">Eval Tests</span>
                        </button>
                      )}
                      <button
                        onClick={() => setShowConfigModal(true)}
                        className="btn-secondary px-4 py-2 flex items-center gap-2"
                        title="Project Settings"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="hidden md:inline">Settings</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Cross-projects Analysis Header (card style) */}
          {isCrossProjectMode && (
            <div className="mb-8 pt-6">
              <div className="relative overflow-hidden" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
                <div className="absolute inset-x-0 top-0 h-px" style={{backgroundColor: 'var(--tqa-green)'}} />
                <div className="p-5 md:p-6">
                  <h2 className="brand-title text-2xl md:text-3xl mb-1">Cross-projects Analysis</h2>
                  <p className="max-w-3xl" style={{color: 'rgba(255,255,255,0.5)'}}>Ask questions that require information from multiple projects.</p>
                </div>
              </div>
            </div>
          )}

          {/* Configuration Header (card style) */}
          {isConfigurationMode && (
            <div className="mb-8 pt-6">
              <div className="relative overflow-hidden" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
                <div className="absolute inset-x-0 top-0 h-px" style={{backgroundColor: 'var(--tqa-green)'}} />
                <div className="p-5 md:p-6">
                  <h2 className="brand-title text-2xl md:text-3xl mb-1">Application Configuration</h2>
                  <p className="max-w-3xl" style={{color: 'rgba(255,255,255,0.5)'}}>Configure system settings, AI models, and manage analysis prompts for optimal performance.</p>
                </div>
              </div>
            </div>
          )}

          {/* Dashboard Header (card style) */}
          {isDashboardMode && (
            <div className="mb-8 pt-6">
              <div className="relative overflow-hidden" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
                <div className="absolute inset-x-0 top-0 h-px" style={{backgroundColor: 'var(--tqa-green)'}} />
                <div className="p-5 md:p-6">
                  <h2 className="brand-title text-2xl md:text-3xl mb-1">Admin Usage Dashboard</h2>
                  <p className="max-w-3xl" style={{color: 'rgba(255,255,255,0.5)'}}>Track app usage, cost, and model activity across projects, users, and time.</p>
                </div>
              </div>
            </div>
          )}

          {/* Tab Content */}
          <div
            className={`flex-1 min-h-0 ${isFolderBrowsingMode ? 'pt-6' : ''} ${(isDashboardMode || isConfigurationMode) ? 'overflow-y-auto' : 'overflow-hidden'}`}
          >
            {renderTabContent()}
          </div>
        </div>
      </main>

      {/* Project Configuration Modal */}
      {showConfigModal && !isCrossProjectMode && !isConfigurationMode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
            <div className="sticky top-0 px-6 py-4 flex items-center justify-between z-10" style={{backgroundColor: 'var(--tqa-surface)', borderBottom: '1px solid var(--tqa-border-dark)'}}>
              <h3 className="text-xl font-semibold" style={{color: 'rgba(255,255,255,0.9)'}}>Project Configuration</h3>
              <button
                onClick={handleCloseConfigModal}
                className="transition-colors" style={{color: 'rgba(255,255,255,0.4)'}}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-6 overflow-y-auto max-h-[calc(90vh-73px)]">
              <ProjectConfigTab projectId={projectId} onClose={handleCloseConfigModal} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectHub;